/**
 * Theme hook — owns the light/dark switch.
 *
 * Initial state priority:
 *   1. Stored explicit choice in localStorage (aiprompt:theme:v1)
 *   2. System `prefers-color-scheme`
 *   3. Light
 *
 * Applies the chosen theme via `data-theme` attribute on <html>; the CSS
 * in styles.css branches off `:root[data-theme="dark"]`. An inline
 * snippet in index.html sets the same attribute before React mounts so
 * the page does not flash the wrong palette during boot.
 *
 * theme-color meta tag is kept in sync so the iOS / Android browser
 * chrome (status bar tint, address bar) matches the active palette.
 */
import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'aiprompt:theme:v1';
const META_LIGHT = '#4f46e5';
const META_DARK = '#0f1118';

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage unavailable; fall through */
  }
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', theme === 'dark' ? META_DARK : META_LIGHT);
  }
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme());

  // Apply on mount + every change, and persist the explicit choice.
  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  // Follow the system if the user has never made an explicit choice.
  // The localStorage write above means once the user toggles, the system
  // listener no longer overrides them.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    // If there's already a stored value (including the one we just wrote
    // on mount above), the user is in manual mode — skip the listener.
    if (stored) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  return { theme, toggle };
}
